import { useAuth0 } from "@auth0/auth0-react";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import ReactGA from "react-ga4";
import { RouterProvider } from "react-router-dom";

import { DialogProvider } from "@/common/components/dialog/dialog";
import { Failed } from "@/common/components/info/info";
import { Intercom } from "@/common/components/intercom/intercom";
import { Mixpanel } from "@/common/components/mixpanel/mixpanel";
import { Spinner } from "@/common/components/spinner/spinner";
import { getFirstIncompleteCompany } from "@/common/hooks/use-first-company";
import { useUserRefresh } from "@/common/hooks/use-incomplete-user-refresh";
import {
  CompanyProvider,
  useCompany,
} from "@/common/providers/company-provider";
import {
  FeatureProvider,
  useFeatureProvider,
} from "@/common/providers/feature-provider";
import { I18nProvider } from "@/common/providers/i18n-provider";
import { ToastProvider } from "@/common/providers/toast-provider";
import { ToggleProvider } from "@/common/providers/toggle-provider";
import { useTokenStore } from "@/common/providers/token-provider";
import { defaultLanguage, fallbackLanguage } from "@/common/services/i18n";
import { logger } from "@/common/services/logger";
import {
  getMaybeUser,
  getUser,
  getUserState,
  PlanSchema,
} from "@/common/services/user";
import { invariant } from "@/common/utils/invariant";
import { Nullable } from "@/types";

import {
  createAnonymousRouter,
  createBlockedUserRouter,
  createIncompleteUserRouter,
  createRouter,
} from "./routes";

const Setup = ({ children }: React.PropsWithChildren) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const tokenUpdateFn = useTokenStore((state) => state.updateFn);
  const setUpdateTokenFn = useTokenStore((state) => state.setUpdateFn);

  React.useEffect(() => {
    if (tokenUpdateFn) {
      return;
    }
    setUpdateTokenFn(getAccessTokenSilently, loginWithRedirect);
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    setUpdateTokenFn,
    tokenUpdateFn,
  ]);

  if (tokenUpdateFn) {
    return children;
  }

  return (
    <I18nProvider language={defaultLanguage}>
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    </I18nProvider>
  );
};

const App = () => {
  const { user: _user, userState } = useUserRefresh();

  React.useEffect(() => {
    if (_user) {
      ReactGA.set({ user_id: _user.sub });
      logger.setUser({
        email: _user.email,
        id: _user.email,
        username: _user.name,
      });
      logger.setContext(_user);
    } else {
      logger.setUser(null);
      ReactGA.set({ user_id: null });
    }
  }, [_user]);

  if (!userState) {
    return (
      <I18nProvider language={defaultLanguage}>
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      </I18nProvider>
    );
  }

  if (userState === "invalid") {
    return (
      <I18nProvider language={defaultLanguage}>
        <DialogProvider>
          <StableRouter create={createAnonymousRouter} />
        </DialogProvider>
      </I18nProvider>
    );
  }

  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(_user, "User should be defined");

  if (getUserState(_user) === "complete") {
    const user = getUser(_user);
    const language =
      user.user_metadata.can.settings?.language ?? defaultLanguage;
    return (
      <I18nProvider language={language}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <CompanyProvider.Failed error={error} />
          )}
        >
          <CompanyProvider>
            <UserTree />
          </CompanyProvider>
        </ErrorBoundary>
      </I18nProvider>
    );
  }

  if (getUserState(_user) === "incomplete") {
    const user = getMaybeUser(_user);
    const company = getFirstIncompleteCompany(user);
    const language =
      user.user_metadata?.can?.settings?.language ?? fallbackLanguage;
    return (
      <ToggleProvider userId={company?.companyNumber ?? "-1"}>
        <I18nProvider language={language}>
          <DialogProvider>
            <StableRouter create={createIncompleteUserRouter} />
          </DialogProvider>
          <ToastProvider />
        </I18nProvider>
      </ToggleProvider>
    );
  }
};

const UserTree = () => {
  const { user, company } = useCompany();
  const trackedUser = {
    email: user.email,
    name: user.name,
    phone:
      user.phone.countryCode && user.phone.phoneNumber
        ? user.phone.countryCode + user.phone.phoneNumber
        : undefined,
  };
  const IntercomJsx = <Intercom user={trackedUser} />;

  const MixpanelJsx = (
    <Mixpanel
      company={
        company
          ? {
              country: company.country,
              id: company.id,
              name: company.name,
              planId: company.plan.id,
            }
          : undefined
      }
      user={{ ...trackedUser, id: user.id }}
    />
  );

  if (company?.approvalState === "BLOCKED") {
    return (
      <>
        {IntercomJsx}
        {MixpanelJsx}
        <DialogProvider>
          <StableRouter create={createBlockedUserRouter} />
        </DialogProvider>
      </>
    );
  }

  return (
    <ToggleProvider userId={company?.number ?? "-1"}>
      <ErrorBoundary fallbackRender={({ error }) => <Failed error={error} />}>
        {IntercomJsx}
        {MixpanelJsx}
        <FeatureProvider company={company}>
          <DialogProvider>
            <CompanyRouter planId={company?.plan.id ?? null} />
          </DialogProvider>
        </FeatureProvider>
        <ToastProvider />
      </ErrorBoundary>
    </ToggleProvider>
  );
};

const CompanyRouter = ({
  planId,
}: {
  planId: Nullable<PlanSchema["planId"]>;
}) => {
  const features = useFeatureProvider();
  const router = React.useMemo(
    () => createRouter(planId, features),
    [planId, features],
  );

  return <RouterProvider router={router} />;
};

const StableRouter = ({
  create,
}: {
  create: () => ReturnType<typeof createRouter>;
}) => {
  const [router] = React.useState(create);

  return <RouterProvider router={router} />;
};

export { App, Setup };
